export const initialState = {
    isDarkMode: false,
    mainLayout: 'app',
    theme: 'light',
    menu: 'collapsed-default',
    layout: 'full',
    rtlClass: 'ltr',
    animation: '',
    navbar: 'navbar-sticky',
    locale: 'en',
    sidebar: false,
    languageList: [
        { code: 'zh', name: 'Chinese' },
        { code: 'da', name: 'Danish' },
        { code: 'en', name: 'English' },
        { code: 'fr', name: 'French' },
        { code: 'de', name: 'German' },
        { code: 'el', name: 'Greek' },
        { code: 'hu', name: 'Hungarian' },
        { code: 'it', name: 'Italian' },
        { code: 'ja', name: 'Japanese' },
        { code: 'pl', name: 'Polish' },
        { code: 'pt', name: 'Portuguese' },
        { code: 'ru', name: 'Russian' },
        { code: 'es', name: 'Spanish' },
        { code: 'sv', name: 'Swedish' },
        { code: 'tr', name: 'Turkish' },
        { code: 'ae', name: 'Arabic' },
    ],
    isShowMainLoader: true,
    semidark: false,
};

const MENU_MODES = ['vertical', 'collapsible-vertical', 'horizontal', 'collapsed-default'];
const isMenuMode = (v: unknown) => typeof v === 'string' && MENU_MODES.includes(v.trim());

function resolveDarkMode(theme: string, currentDarkMode: boolean): boolean {
    if (theme === 'light') {
        return false;
    }
    if (theme === 'dark') {
        return true;
    }
    if (theme === 'system') {
        return Boolean(window.matchMedia?.('(prefers-color-scheme: dark)').matches);
    }
    return currentDarkMode;
}

function enableDarkModeClass(): void {
    document.querySelector('body')?.classList.add('dark');
}

function disableDarkModeClass(): void {
    document.querySelector('body')?.classList.remove('dark');
}

function handleToggleDirection(state: typeof initialState, payload: string) {
    localStorage.setItem('direction', payload);
    document.querySelector('html')?.setAttribute('dir', payload || 'ltr');
    return { ...state, direction: payload };
}

function handleToggleTheme(state: typeof initialState, payload: string | undefined) {
    const theme = payload ?? state.theme;
    localStorage.setItem('theme', theme);
    const isDarkMode = resolveDarkMode(theme, state.isDarkMode || false);
    if (isDarkMode) {
        enableDarkModeClass();
    } else {
        disableDarkModeClass();
    }
    return { ...state, theme, isDarkMode };
}

function handleToggleMenu(state: typeof initialState, payload: string | undefined) {
    const nextMenu = String(payload ?? state.menu).trim();
    localStorage.setItem('menu', nextMenu);
    const nextLayout = isMenuMode(state.layout) ? 'full' : state.layout;
    if (nextLayout !== state.layout) {
        localStorage.setItem('layout', nextLayout);
    }
    return { ...state, sidebar: false, menu: nextMenu, layout: nextLayout };
}

function handleToggleLayout(state: typeof initialState, payload: string | undefined) {
    const nextLayout = isMenuMode(payload) ? 'full' : String(payload ?? state.layout).trim();
    localStorage.setItem('layout', nextLayout);
    return { ...state, layout: nextLayout };
}

function handleToggleRTL(state: typeof initialState, payload: string | undefined) {
    const rtlClass = payload ?? state.rtlClass;
    localStorage.setItem('rtlClass', rtlClass);
    document.querySelector('html')?.setAttribute('dir', rtlClass || 'ltr');
    return { ...state, rtlClass };
}

function handleToggleAnimation(state: typeof initialState, payload: string | undefined) {
    const animation = payload?.trim();
    localStorage.setItem('animation', animation ?? '');
    if (animation) {
        const eleanimation: Element | null = document.querySelector('.animation');
        eleanimation?.classList.add('animate__animated');
        eleanimation?.classList.add(animation);
    }
    return { ...state, animation: animation ?? '' };
}

export function indexReducer(state = initialState, action: { type: string; payload?: any }) {
    switch (action.type) {
        case 'toggleDirection':
            return handleToggleDirection(state, action.payload);
        case 'toggleMainLoader':
            return { ...state, isShowMainLoader: action.payload };
        case 'setMainLayout':
            return { ...state, mainLayout: action.payload };
        case 'toggleTheme':
            return handleToggleTheme(state, action.payload);
        case 'toggleMenu':
            return handleToggleMenu(state, action.payload);
        case 'toggleLayout':
            return handleToggleLayout(state, action.payload);
        case 'toggleRTL':
            return handleToggleRTL(state, action.payload);
        case 'toggleAnimation':
            return handleToggleAnimation(state, action.payload);
        case 'toggleNavbar':
            localStorage.setItem('navbar', action.payload || state.navbar);
            return { ...state, navbar: action.payload || state.navbar };
        case 'toggleSemidark':
            localStorage.setItem('semidark', String(action.payload ?? false));
            return { ...state, semidark: action.payload ?? false };
        case 'toggleLocale':
            localStorage.setItem('i18n_locale', action.payload || state.locale);
            return { ...state, locale: action.payload || state.locale };
        case 'toggleSidebar':
            return { ...state, sidebar: !state.sidebar };
        default:
            return state;
    }
}
